import { jsxs, jsx } from "react/jsx-runtime";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, IconButton, Dialog, Slide } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDragControls, motion } from "motion/react";
import React__default, { useRef } from "react";
const StyledDialog = styled(Dialog)(({ theme }) => {
  var _a, _b, _c, _d, _e, _f;
  return {
    "& .MuiDialog-paper": {
      backgroundColor: ((_c = (_b = (_a = theme.vars) == null ? void 0 : _a.palette) == null ? void 0 : _b.background) == null ? void 0 : _c.paper) ?? theme.palette.background.paper,
      boxShadow: "0 5px 10px 0 rgba(181, 176, 176, 0.16)",
      paddingTop: "24px",
      transition: "all 0.3s ease-in-out"
    },
    "&.MuiDialog-root": {
      alignItems: "center",
      // Center vertically
      justifyContent: "center"
      // Center horizontally
    },
    // Mobile styles
    [theme.breakpoints.down("md")]: {
      "& .MuiDialog-paper": {
        position: "absolute",
        backgroundColor: ((_f = (_e = (_d = theme.vars) == null ? void 0 : _d.palette) == null ? void 0 : _e.background) == null ? void 0 : _f.paper) ?? theme.palette.background.paper,
        bottom: 0,
        left: 0,
        right: 0,
        margin: 0,
        boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.1)",
        paddingTop: "12px"
        // Add padding for the handle
      },
      "&.MuiDialog-root": {
        alignItems: "flex-end",
        width: "100%"
      }
    }
  };
});
const DragHandle = styled("div")(() => ({
  width: "40px",
  height: "5px",
  backgroundColor: "#CCCCCC",
  borderRadius: "4px",
  margin: "0 auto",
  position: "absolute",
  top: "8px",
  left: "calc(50% - 20px)",
  zIndex: 10
  // Ensure it's above other content
}));
const SlideUpTransition = React__default.forwardRef(function Transition(props, ref) {
  return /* @__PURE__ */ jsx(Slide, { direction: "up", ref, ...props });
});
const StyledDialogPaper = styled(motion.div)(({ theme }) => ({
  width: "fit-content",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  borderTopRightRadius: "10px",
  borderTopLeftRadius: "10px",
  borderBottomRightRadius: "10px",
  borderBottomLeftRadius: "10px",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "100%",
    borderTopRightRadius: "8px",
    borderTopLeftRadius: "8px",
    borderBottomRightRadius: "0px",
    borderBottomLeftRadius: "0px"
  }
}));
const MotionDialogPaper = React__default.forwardRef(function DialogPaper(props, ref) {
  return /* @__PURE__ */ jsx(
    StyledDialogPaper,
    {
      ref,
      drag: props.drag,
      dragControls: props.dragControls,
      dragConstraints: props.dragConstraints,
      dragElastic: 0.2,
      dragTransition: {
        bounceStiffness: 300,
        bounceDamping: 30,
        timeConstant: 400
      },
      onDragEnd: props.onDragEnd,
      ...props
    }
  );
});
const ResponsiveDialog = ({
  open,
  onClose,
  children,
  ...dialogProps
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const TransitionComponent = isMobile ? SlideUpTransition : void 0;
  const dragControls = useDragControls();
  const constraintsRef = useRef(null);
  const startDrag = (event) => {
    if (!isMobile) return;
    dragControls.start(event);
  };
  return /* @__PURE__ */ jsxs(
    StyledDialog,
    {
      open,
      onClose,
      TransitionComponent,
      PaperComponent: MotionDialogPaper,
      PaperProps: {
        // Motion props for mobile swipe behavior
        ...isMobile && {
          drag: "y",
          dragControls,
          dragConstraints: { top: 0, bottom: 100 },
          // Reduce bottom constraint for better bounce
          dragElastic: 0.2,
          dragTransition: {
            bounceStiffness: 600,
            // Higher value for more pronounced bounce
            bounceDamping: 30,
            // Lower value allows more bounce oscillation
            timeConstant: 300
          },
          dragSnapToOrigin: true,
          // This is the key for auto bounce back!
          onDragEnd: (event, info) => {
            if (info.offset.y > 150 || info.velocity.y > 300) {
              onClose == null ? void 0 : onClose();
            }
          },
          ref: constraintsRef
        }
      },
      ...dialogProps,
      children: [
        isMobile ? /* @__PURE__ */ jsx(
          "div",
          {
            className: "bc_dialog_drag_handle",
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              marginTop: "8px",
              height: "40px",
              touchAction: "none",
              zIndex: 1,
              cursor: "grab"
            },
            onPointerDown: startDrag,
            children: /* @__PURE__ */ jsx(DragHandle, {})
          }
        ) : onClose ? /* @__PURE__ */ jsx(
          "div",
          {
            className: "bc_dialog_close_button",
            style: {
              position: "absolute",
              top: 15,
              right: 14,
              height: "40px",
              width: "40px",
              touchAction: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1
            },
            onPointerDown: startDrag,
            children: /* @__PURE__ */ jsx(
              IconButton,
              {
                "aria-label": "close",
                onClick: onClose,
                disableRipple: true,
                sx: {
                  all: "unset",
                  width: "24px",
                  height: "24px",
                  "&:hover": {
                    backgroundColor: "transparent"
                  }
                },
                children: /* @__PURE__ */ jsx(CloseIcon, { sx: (theme) => {
                  var _a, _b, _c;
                  return { color: ((_c = (_b = (_a = theme.vars) == null ? void 0 : _a.palette) == null ? void 0 : _b.text) == null ? void 0 : _c.primary) ?? theme.palette.text.primary };
                } })
              }
            )
          }
        ) : null,
        children
      ]
    }
  );
};
export {
  ResponsiveDialog
};
//# sourceMappingURL=ResponsiveDialog.js.map
